<script setup lang="ts">
import { type HTMLAttributes, computed } from 'vue'
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from 'radix-vue'
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour'
import { CmsDepth } from '@pkg/config/misc/cmsDepth'
import { X } from 'lucide-vue-next'
import { cn } from '../../../lib/utils'

const props = defineProps<DialogContentProps & { class?: HTMLAttributes['class'] }>()
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal :data-cms-depth="CmsDepth.Modal">
    <DialogOverlay
      class="fixed inset-0 z-[50] bg-black/60  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
    <DialogContent
      v-bind="forwarded"
      :data-cms-depth="CmsDepth.Modal"
      :class="
        cn(
          'fixed left-1/2 top-1/2 z-[50] grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-[16px] bg-beige-30 p-[24px] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          props.class,
        )"
    >
      <slot />

      <DialogClose :data-analytics-click-behaviour="AnalyticsClickBehaviour.ModalClose"
        class="amp-auto-track absolute right-[4px] top-[4px] text-black rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-green focus:ring-offset-2 disabled:pointer-events-none"
      >
        <X class="w-[20px] h-[20px]" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
